/**
 * Design constants for Card Deck component
 * Values extracted from Figma design
 */

export const CARD_DECK_COLORS = {
  // Card backgrounds
  cardBackground: '#FFFFFF',
  
  // Overlay tints for stacked cards
  middleCardOverlay: 'rgba(1, 167, 200, 0.1)',
  backCardOverlay: 'rgba(1, 167, 200, 0.2)',
  
  // Text colors
  titleText: '#1B2831',       // Gray 6
  descriptionText: '#4D5762', // Gray 4
  strengthensText: '#323B46', // Gray 5
  skillText: '#01A7C8',       // Primary Blue
  
  // Button
  buttonBackground: '#1B2831',
  buttonText: '#FFFFFF',
  
  // Badge
  sponsoredBadgeBg: '#1B2831',
  sponsoredBadgeText: '#FFFFFF',
  
  // Instruction text
  instructionText: '#4D5762',
} as const;

export const CARD_DECK_DIMENSIONS = {
  // Front card (100% scale)
  frontCard: {
    width: 382,
    height: 462,
    scale: 1,
  },
  // Middle card (~84% scale)
  middleCard: {
    width: 321,
    height: 388,
    scale: 0.84,
    offsetY: -22 + 10, // pixels ABOVE front card (negative = upward)
  },
  // Back card (~70% scale)
  backCard: {
    width: 268,
    height: 324,
    scale: 0.70,
    offsetY: -34 + 11, // pixels ABOVE front card (negative = upward)
  },
  // Image inside card
  imageHeight: 180,
  // Button
  buttonHeight: 48,
} as const;

export const CARD_DECK_RADIUS = {
  card: 24,
  image: 12,
  button: 8,
  likeBadge: 12,
  sponsoredBadge: 12,
  avatar: 170, // Circular
} as const;

export const CARD_DECK_SHADOWS = {
  card: '0px 4px 16px rgba(0, 117, 141, 0.2)',
} as const;

export const CARD_DECK_SPACING = {
  cardPadding: 16,
  cardGap: 10, // Gap between card content sections
  metadataGap: 16, // Gap between metadata items
} as const;

export const CARD_DECK_SWIPE = {
  threshold: 100, // pixels - distance to trigger swipe complete
  exitDistance: 500, // pixels - how far card travels when exiting
  snapBackDuration: 0.3, // seconds
  exitDuration: 0.3, // seconds
  promoteDuration: 0.4, // seconds - card promotion animation
} as const;

export const CARD_DECK_RESPONSIVE = {
  // Container widths matching FilterBar pattern
  containerWidth: {
    base: 382,      // Mobile: < 640px
    sm: 400,        // Small tablet: ≥ 640px
    md: 440,        // Tablet: ≥ 768px
    lg: 480,        // Desktop: ≥ 1024px
  },
  
  // Aspect ratio constant (width:height)
  aspectRatio: 382 / 462,  // ~0.8268
  
  // Calculated heights based on aspect ratio
  containerHeight: {
    base: 462,      // 382 / 0.8268 = 462
    sm: 484,        // 400 / 0.8268 = 484
    md: 532,        // 440 / 0.8268 = 532
    lg: 580,        // 480 / 0.8268 = 580
  },
  
  // Image heights (proportional scaling ~39% of card height)
  imageHeight: {
    base: 180,      // Mobile (382px card)
    sm: 189,        // Small tablet (400px card)
    md: 207,        // Tablet (440px card)
    lg: 226,        // Desktop (480px card)
  },
  
  // Button heights
  buttonHeight: {
    base: 48,       // Mobile
    sm: 50,         // Small tablet
    md: 52,         // Tablet
    lg: 56,         // Desktop
  },
  
  // Padding inside cards
  cardPadding: {
    base: 16,       // Mobile
    sm: 18,         // Small tablet
    md: 20,         // Tablet
    lg: 22,         // Desktop
  },
  
  // Gap between card sections
  cardGap: {
    base: 10,       // Mobile
    sm: 11,         // Small tablet
    md: 12,         // Tablet
    lg: 14,         // Desktop
  },
} as const;

export const JOURNEY_CARD = {
  padding: 'p-4 sm:p-[18px] md:p-5 lg:p-[22px]',
  gap: 'gap-4 sm:gap-[18px] md:gap-5 lg:gap-[22px]',
  background: 'linear-gradient(180deg, rgb(8, 170, 202) 0%, rgb(219, 247, 248) 100%)',
  border: '2px solid #FFFFFF',
  textColor: '#1B2831',
  typography: {
    title: 'text-[22px] sm:text-[23px] md:text-[24px] lg:text-[26px] font-bold tracking-[-0.04em]',
    subtitle: 'text-[16px] sm:text-[17px] md:text-[18px] lg:text-[19px] font-normal',
  },
  button: {
    label: `let's personalise your sparks`,
    letterSpacing: '0.72px',
    heightClasses: 'h-12 sm:h-[50px] md:h-[52px] lg:h-14',
  },
  illustration: {
    borderRadius: 12,
  },
} as const;

export const INTRO_CARD = {
  padding: 'p-4 sm:p-[18px] md:p-5 lg:p-[22px]',
  gap: 'gap-4 sm:gap-[18px] md:gap-5 lg:gap-[22px]',
  typography: {
    title: 'text-[24px] sm:text-[25px] md:text-[26px] lg:text-[28px] font-bold tracking-[-0.04em]',
    subtitle: 'text-[16px] sm:text-[17px] md:text-[18px] lg:text-[19px] font-normal',
  },
} as const;

export const CARD_DECK_TYPOGRAPHY = {
  title: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '100%',
    sizeClasses: 'text-[18px] sm:text-[19px] md:text-[20px] lg:text-[22px]',
  },
  description: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '100%',
    sizeClasses: 'text-[13px] sm:text-[14px] md:text-[15px] lg:text-[17px]',
  },
  metadata: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '100%',
    sizeClasses: 'text-[10px] sm:text-[11px] md:text-[12px] lg:text-[13px]',
  },
  button: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: 900,
    letterSpacing: '0.72px',
    textTransform: 'uppercase' as const,
    sizeClasses: 'text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px]',
  },
  recommendationTitle: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '16px',
    fontWeight: 900,
    letterSpacing: '-0.32px',
    sizeClasses: 'text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px]',
  },
  instruction: {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '100%',
    sizeClasses: 'text-[11px] sm:text-xs md:text-[13px] lg:text-[15px]',
  },
} as const;
